/**
 * Small deterministic PRNG based on mulberry32.
 *
 * It is intended for repeatable simulation decisions, not cryptography.
 */
export class SeededRandom {
  private state: number;

  constructor(seed: number) {
    if (!Number.isFinite(seed)) {
      throw new TypeError("Random seed must be finite");
    }
    this.state = seed >>> 0;
  }

  next(): number {
    this.state = (this.state + 0x6d2b79f5) >>> 0;
    let value = this.state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4_294_967_296;
  }

  integer(minInclusive: number, maxExclusive: number): number {
    if (
      !Number.isSafeInteger(minInclusive) ||
      !Number.isSafeInteger(maxExclusive) ||
      maxExclusive <= minInclusive
    ) {
      throw new RangeError("Random integer range must be non-empty");
    }

    return (
      minInclusive + Math.floor(this.next() * (maxExclusive - minInclusive))
    );
  }

  pick<T>(items: readonly T[]): T {
    if (items.length === 0) {
      throw new RangeError("Cannot pick from an empty collection");
    }
    return items[this.integer(0, items.length)]!;
  }
}
